const DEFAULT_DOWN_RULE = 3;
const DEFAULT_UP_RULE = 1;
function clamp(v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
}
function currentStep(config, idx) {
    const phases = config.stepSizesByPhase;
    if (phases.length === 0) {
        throw new Error('GameStaircaseConfig.stepSizesByPhase must contain at least one step size');
    }
    const safeIdx = Math.min(idx, phases.length - 1);
    const step = phases[safeIdx];
    if (step === undefined) {
        throw new Error(`Invalid step phase index ${safeIdx}`);
    }
    return step;
}
/**
 * Fallback estimate si maxTrials atteint sans convergence.
 * Médiane des 20 derniers trials (robust simple).
 */
function fallbackThreshold(trials) {
    const slice = trials.slice(-20).map((t) => t.value).sort((a, b) => a - b);
    if (slice.length === 0)
        return 0;
    const mid = Math.floor(slice.length / 2);
    return slice[mid] ?? 0;
}
export function init(config, calibration, priorState) {
    if (priorState &&
        priorState.config.gameSlug === config.gameSlug &&
        priorState.thresholdEstimate !== null) {
        // Resume warm-start : on redémarre près du seuil précédent
        const warmStart = priorState.thresholdEstimate;
        return {
            config,
            trialIdx: 0,
            currentValue: clamp(warmStart, config.minValue, config.maxValue),
            // skip phase 0 (largest step) : on a déjà un prior
            currentStepIdx: 1,
            consecutiveCorrect: 0,
            reversalsCount: 0,
            reversalValues: [],
            lastDirection: null,
            trialLog: [],
            done: false,
            thresholdEstimate: null,
        };
    }
    // Cold start (calibration ignored in v1 — réservé v2 QUEST priors)
    void calibration;
    return {
        config,
        trialIdx: 0,
        currentValue: config.startValue,
        currentStepIdx: 0,
        consecutiveCorrect: 0,
        reversalsCount: 0,
        reversalValues: [],
        lastDirection: null,
        trialLog: [],
        done: false,
        thresholdEstimate: null,
    };
}
export function getNextStimulus(state) {
    return {
        trialIdx: state.trialIdx,
        value: state.currentValue,
    };
}
/**
 * Application 3-down/1-up.
 * - 3 correct consecutive → harder (move dans direction "harder" selon config.direction)
 * - 1 incorrect → easier immediate
 * Reversal = changement de direction → step size shrinks (advance currentStepIdx).
 * Run done quand reversalsToConverge atteints OU maxTrials atteint.
 */
export function submitResponse(state, response) {
    void DEFAULT_UP_RULE; // documenté dans la signature, on consomme implicitement
    const { config } = state;
    const trialEntry = {
        idx: state.trialIdx,
        value: state.currentValue,
        response,
        direction: 'same',
    };
    let newConsecutive = state.consecutiveCorrect;
    let newValue = state.currentValue;
    let newDirection = state.lastDirection;
    let newReversals = state.reversalsCount;
    let newReversalValues = state.reversalValues;
    let newStepIdx = state.currentStepIdx;
    if (response.correct) {
        newConsecutive += 1;
        if (newConsecutive >= DEFAULT_DOWN_RULE) {
            // 3-down : make harder
            const step = currentStep(config, newStepIdx);
            newValue =
                config.direction === 'down-easier'
                    ? state.currentValue - step
                    : state.currentValue + step;
            newValue = clamp(newValue, config.minValue, config.maxValue);
            newConsecutive = 0;
            const moveDirection = config.direction === 'down-easier' ? 'down' : 'up';
            // Reversal : direction change
            if (newDirection !== null && newDirection !== moveDirection) {
                newReversals += 1;
                newReversalValues = [...newReversalValues, state.currentValue];
                newStepIdx = Math.min(newStepIdx + 1, config.stepSizesByPhase.length - 1);
            }
            newDirection = moveDirection;
            trialEntry.direction = moveDirection;
        }
    }
    else {
        // 1-up : make easier immediately
        const step = currentStep(config, newStepIdx);
        newValue =
            config.direction === 'down-easier'
                ? state.currentValue + step
                : state.currentValue - step;
        newValue = clamp(newValue, config.minValue, config.maxValue);
        newConsecutive = 0;
        const moveDirection = config.direction === 'down-easier' ? 'up' : 'down';
        if (newDirection !== null && newDirection !== moveDirection) {
            newReversals += 1;
            newReversalValues = [...newReversalValues, state.currentValue];
            newStepIdx = Math.min(newStepIdx + 1, config.stepSizesByPhase.length - 1);
        }
        newDirection = moveDirection;
        trialEntry.direction = moveDirection;
    }
    const trialIdxNext = state.trialIdx + 1;
    const reachedReversals = newReversals >= config.reversalsToConverge;
    const reachedMaxTrials = trialIdxNext >= config.maxTrials;
    const runDone = reachedReversals || reachedMaxTrials;
    let thresholdEstimate = null;
    let fallback = false;
    if (runDone) {
        if (reachedReversals) {
            const lastK = newReversalValues.slice(-config.reversalsForThreshold);
            thresholdEstimate = lastK.reduce((a, b) => a + b, 0) / lastK.length;
        }
        else {
            // maxTrials hit without convergence — robust median fallback
            thresholdEstimate = fallbackThreshold([...state.trialLog, trialEntry]);
            fallback = true;
        }
    }
    const nextState = {
        ...state,
        trialIdx: trialIdxNext,
        currentValue: newValue,
        currentStepIdx: newStepIdx,
        consecutiveCorrect: newConsecutive,
        reversalsCount: newReversals,
        reversalValues: newReversalValues,
        lastDirection: newDirection,
        trialLog: [...state.trialLog, trialEntry],
        done: runDone,
        thresholdEstimate,
    };
    if (fallback)
        nextState.fallback = true;
    return {
        state: nextState,
        feedback: response.correct ? 'correct' : 'incorrect',
        runDone,
    };
}
export function getThreshold(state) {
    return state.thresholdEstimate;
}
export function serialize(state) {
    return JSON.stringify(state);
}
export function deserialize(blob) {
    return JSON.parse(blob);
}
export const threeDownOneUpEngine = {
    init,
    getNextStimulus,
    submitResponse,
    getThreshold,
    serialize,
    deserialize,
};
//# sourceMappingURL=threeDownOneUp.js.map